/**
 * The ONLY file that touches the Spotware WebView packages.
 *
 * There are TWO of them and the naming misleads:
 *   @spotware-web-team/sdk-external-api  0.0.6   the postMessage transport only
 *   @spotware-web-team/sdk               0.0.20  every method, taking that transport first
 *
 * Both are 0.0.x and will break. Everything else in the app talks to the interface below
 * instead, which keeps the blast radius of an SDK change to this one file. Do not import
 * either package anywhere else.
 *
 * Handshake: createClientAdapter → handleConfirmEvent → registerEvent → handleConfirmEvent.
 * The host answers nothing until it completes. Measured: 26 ms on the web, 29 ms on iOS.
 *
 * Two shapes coexist on the wire (docs/07-webview-spike.md): responses arrive as the inner
 * envelope with PascalCase fields one level deeper than the .d.ts says, while events match
 * their declared camelCase types. Both are normalised HERE and nowhere else.
 */
import { createClientAdapter } from '@spotware-web-team/sdk-external-api'
import {
  createNewOrder,
  executionEvent,
  getAccountInformation,
  getLightSymbolList,
  getSymbol,
  handleConfirmEvent,
  openInternalUrl,
  quoteEvent,
  registerEvent,
  subscribeQuotes,
} from '@spotware-web-team/sdk'
import { createLogger } from '@veksa/logger'
import { Observable, firstValueFrom, map, filter, take, timeout } from 'rxjs'

export interface Quote {
  /** Our own naming. The WebView SDK says `id`, the Open API says `symbolId` — the
   * mapping between the two vocabularies belongs here and nowhere else. */
  symbolId: number
  /** Descaled by the symbol's digits. Either side may be missing on a given event. */
  bid: number | null
  ask: number | null
}

export interface ExecutionUpdate {
  executionType: string
  positionId: number | null
  orderId: number | null
  label: string | null
  clientOrderId: string | null
  errorCode: string | null
}

export interface AccountInfo {
  /**
   * The ACTIVE account, as the SDK names it: the broker login the trader sees in cTrader's own
   * selector. It is NOT the guardian's account id — this channel does not carry one — so the
   * plugin resolves it through the API before it can ask about anything. Cross-account
   * aggregates come from the guardian API; the SDK cannot see the trader's other accounts.
   */
  login: number
  balance: number
  moneyDigits: number
}

/**
 * The account id the guardian keys everything by: snapshots, rule config, events, order
 * intents. That is `ctidTraderAccountId` and nothing else.
 *
 * The SDK also sends `traderId`, cTrader's internal row id, and this used to prefer it — so
 * the plugin asked for account 3259237 while the guardian published for 46246548, the same
 * account under the identifier the Open API uses. Nothing errored: the ticket simply sat at
 * "waiting for the guardian" forever, on every account, because no snapshot by that id has
 * ever existed. Found on the box, 2026-09-18.
 *
 * There is deliberately no fallback. A plausible-looking wrong number binds the ticket to an
 * account the guardian has never heard of, which is the failure that hides; an error says so.
 */
export function accountLoginOf(trader: Record<string, unknown> | undefined): number {
  const login = field<number>(trader, 'login')
  if (login === undefined) {
    // Name what did arrive. "Unknown shape" sends whoever reads it to the debugger; the field
    // list sends them to the answer, and this SDK is pre-release enough to change under us.
    const keys = trader && typeof trader === 'object' ? Object.keys(trader).join(', ') : 'nothing'
    throw new Error(`account information carried no login — cannot identify the account. Received: ${keys}`)
  }
  return login
}

/** Every quote on this channel is an integer of 1/100000 of a unit of price. */
const QUOTE_SCALE = 100_000

export interface SymbolInfo {
  symbolId: number
  name: string
  digits: number
  lotSize: number
  minVolume: number
  maxVolume: number
  stepVolume: number
}

export interface PlaceOrderRequest {
  symbolId: number
  side: 'Buy' | 'Sell'
  /** cTrader units × 100. */
  volume: number
  stopLoss?: number
  takeProfit?: number
  /** `gk:<intent id>` from Tier 3 — what the guardian reads to attribute the fill. */
  label: string
  clientOrderId: string
}

export interface GatekeeperHost {
  /** Resolves once the three-step handshake has completed. */
  connect(): Promise<void>
  disconnect(): void

  getAccount(): Promise<AccountInfo>
  listSymbols(): Promise<{ symbolId: number; name: string }[]>
  getSymbol(symbolId: number): Promise<SymbolInfo>
  subscribeQuotes(symbolIds: number[]): Observable<Quote>
  executions(): Observable<ExecutionUpdate>

  /**
   * The send. After Tier 3 has recorded the intent as cleared, the plugin — not any server —
   * places the order in the trader's own session. This is the design, not a fallback
   * (docs/02-architecture.md, corrected 2026-09-07): no server component holds an entry capability.
   */
  placeOrder(request: PlaceOrderRequest): Promise<ExecutionUpdate | null>

  /** Opens a URL through the host — the consent page must not be framed. */
  openUrl(url: string): Promise<boolean>
}

type Transport = ReturnType<typeof createClientAdapter>

// The SDK's own logger, verbose only in development — its chatter is the raw protocol.
const quietLogger = createLogger(import.meta.env.DEV)

/** Reads a response that may arrive declared (camelCase) or as the inner envelope (PascalCase). */
function unwrap<T extends object>(res: unknown, key: string): T | undefined {
  const declared = (res as Record<string, unknown>)[key]
  if (declared !== undefined) return declared as T
  const inner = (res as { payload?: Record<string, unknown> }).payload
  if (!inner) return undefined
  const pascal = key.charAt(0).toUpperCase() + key.slice(1)
  return (inner[pascal] ?? inner[key]) as T | undefined
}

function field<T>(obj: unknown, camel: string): T | undefined {
  if (!obj || typeof obj !== 'object') return undefined
  const record = obj as Record<string, unknown>
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1)
  return (record[camel] ?? record[pascal]) as T | undefined
}

export function createCTraderHost(): GatekeeperHost {
  let transport: Transport | null = null
  const digitsBySymbol = new Map<number, number>()
  // Still needed for rounding a price to the symbol's own precision; never for descaling.

  const t = (): Transport => {
    if (!transport) throw new Error('SDK not connected — call connect() first')
    return transport
  }

  return {
    async connect() {
      transport = createClientAdapter({ logger: quietLogger })
      const tr = transport
      await firstValueFrom(handleConfirmEvent(tr, {}).pipe(take(1)), { defaultValue: undefined })
      await firstValueFrom(registerEvent(tr).pipe(take(1), timeout(10_000)))
      await firstValueFrom(handleConfirmEvent(tr, {}).pipe(take(1)), { defaultValue: undefined })
    },

    disconnect() {
      transport = null
    },

    async getAccount() {
      const res = await firstValueFrom(getAccountInformation(t(), {}).pipe(take(1), timeout(5_000)))
      const trader = unwrap<Record<string, unknown>>(res, 'trader')
      return {
        login: accountLoginOf(trader),
        balance: field<number>(trader, 'balance') ?? 0,
        moneyDigits: field<number>(trader, 'moneyDigits') ?? 2,
      }
    },

    async listSymbols() {
      const res = await firstValueFrom(getLightSymbolList(t(), {}).pipe(take(1), timeout(5_000)))
      const list = unwrap<unknown[]>(res, 'symbol') ?? []
      return list
        .map((s) => ({ symbolId: field<number>(s, 'id') ?? 0, name: field<string>(s, 'name') ?? '' }))
        .filter((s) => s.symbolId > 0)
    },

    async getSymbol(symbolId) {
      const res = await firstValueFrom(getSymbol(t(), { symbolId: [symbolId] }).pipe(take(1), timeout(5_000)))
      const first = (unwrap<unknown[]>(res, 'symbol') ?? [])[0]
      const digits = field<number>(first, 'digits') ?? 5
      digitsBySymbol.set(symbolId, digits)
      return {
        symbolId,
        name: field<string>(first, 'name') ?? '',
        digits,
        // The SDK reports lotSize in the same x100 scale as volume ("cents"), while
        // everything downstream — lotsOf, the fake host, the tests — counts units per lot.
        // Passing it through unconverted made every lot count 100x too small: 3 units of
        // XAUUSD rendered as "0.00 lots" instead of 0.03.
        lotSize: (field<number>(first, 'lotSize') ?? 10_000_000) / 100,
        minVolume: field<number>(first, 'minVolume') ?? 100_000,
        maxVolume: field<number>(first, 'maxVolume') ?? 1_000_000_000,
        stepVolume: field<number>(first, 'stepVolume') ?? field<number>(first, 'minVolume') ?? 100_000,
      }
    },

    subscribeQuotes(symbolIds) {
      const tr = t()
      subscribeQuotes(tr, { symbolId: symbolIds }).pipe(take(1)).subscribe({ error: () => undefined })
      return quoteEvent(tr).pipe(
        filter((q) => symbolIds.includes(q.symbolId)),
        map((q) => {
          // Quote prices are scaled by a FIXED 10^5, not by the symbol's digits — the same
          // convention ProtoOASpotEvent uses on the Open API side ("1/100000 of a unit of
          // price"). Descaling by digits is right for a 5-digit FX pair and silently wrong
          // everywhere else: XAUUSD (2 digits) rendered 4308140.00 for a gold price of
          // 4308.14, and the ticket sized against it.
          const scale = QUOTE_SCALE
          return {
            symbolId: q.symbolId,
            bid: q.bid === undefined ? null : q.bid / scale,
            ask: q.ask === undefined ? null : q.ask / scale,
          }
        }),
      )
    },

    executions() {
      return executionEvent(t()).pipe(map(toExecution))
    },

    async placeOrder(request) {
      const payload = {
        symbolId: request.symbolId,
        orderType: 1, // ProtoOrderType.MARKET
        tradeSide: request.side === 'Buy' ? 1 : 2, // ProtoTradeSide
        volume: request.volume,
        label: request.label,
        clientOrderId: request.clientOrderId,
        ...(request.stopLoss === undefined ? {} : { stopLoss: request.stopLoss }),
        ...(request.takeProfit === undefined ? {} : { takeProfit: request.takeProfit }),
      }
      const res = await firstValueFrom(
        createNewOrder(t(), payload as Parameters<typeof createNewOrder>[1]).pipe(take(1), timeout(15_000)),
        { defaultValue: undefined },
      )
      return res ? toExecution(res as Parameters<typeof toExecution>[0]) : null
    },

    async openUrl(url) {
      try {
        await firstValueFrom(openInternalUrl(t(), { url } as Parameters<typeof openInternalUrl>[1]).pipe(take(1), timeout(3_000)))
        return true
      } catch {
        return false
      }
    },
  }
}

function toExecution(e: { executionType?: unknown; position?: unknown; order?: unknown; errorCode?: string }): ExecutionUpdate {
  const trade = field<unknown>(e.position, 'tradeData')
  return {
    executionType: String(field<unknown>(e, 'executionType') ?? ''),
    positionId: field<number>(e.position, 'positionId') ?? null,
    orderId: field<number>(e.order, 'orderId') ?? null,
    label: field<string>(trade, 'label') ?? null,
    clientOrderId: field<string>(e.order, 'clientOrderId') ?? null,
    errorCode: e.errorCode ?? null,
  }
}

/**
 * A host for development and tests: one account, a few symbols, quotes on a timer, and a
 * `placeOrder` that answers with a filled execution carrying the label back.
 */
export function createFakeHost(options: { login?: number; bid?: number; ask?: number } = {}): GatekeeperHost {
  const executions: ((e: ExecutionUpdate) => void)[] = []
  const symbols: SymbolInfo[] = [
    { symbolId: 1, name: 'EURUSD', digits: 5, lotSize: 100_000, minVolume: 100_000, maxVolume: 1_000_000_000, stepVolume: 100_000 },
    { symbolId: 41, name: 'XAUUSD', digits: 2, lotSize: 100, minVolume: 100, maxVolume: 100_000_00, stepVolume: 100 },
  ]
  return {
    connect: () => Promise.resolve(),
    disconnect: () => undefined,
    getAccount: () => Promise.resolve({ login: options.login ?? 5_761_218, balance: 1_079_90, moneyDigits: 2 }),
    listSymbols: () => Promise.resolve(symbols.map((s) => ({ symbolId: s.symbolId, name: s.name }))),
    getSymbol: (id) => {
      const found = symbols.find((s) => s.symbolId === id)
      return found ? Promise.resolve(found) : Promise.reject(new Error(`no symbol ${id}`))
    },
    subscribeQuotes: (ids) =>
      new Observable<Quote>((subscriber) => {
        const bid = options.bid ?? 1.1
        const ask = options.ask ?? bid + 0.0001
        const tick = () => ids.forEach((symbolId) => subscriber.next({ symbolId, bid, ask }))
        tick()
        const handle = setInterval(tick, 500)
        return () => clearInterval(handle)
      }),
    executions: () =>
      new Observable<ExecutionUpdate>((subscriber) => {
        const push = (e: ExecutionUpdate) => subscriber.next(e)
        executions.push(push)
        return () => executions.splice(executions.indexOf(push), 1)
      }),
    placeOrder: (request) => {
      const filled: ExecutionUpdate = {
        executionType: 'ORDER_FILLED',
        positionId: Math.floor(Math.random() * 1_000_000),
        orderId: Math.floor(Math.random() * 1_000_000),
        label: request.label,
        clientOrderId: request.clientOrderId,
        errorCode: null,
      }
      setTimeout(() => executions.forEach((push) => push(filled)), 30)
      return Promise.resolve(filled)
    },
    openUrl: () => Promise.resolve(false),
  }
}
